/** Global Imports */
import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
  memo,
} from 'react';
import { useSelector } from 'react-redux';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import Select from 'react-select';
import { DialogTitle, Divider, Select as MuiSelect } from '@material-ui/core';
import makeAnimated from 'react-select/animated';
import Tooltip from '@material-ui/core/Tooltip';
import MenuItem from '@material-ui/core/MenuItem';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import {
  DisabledButton,
  PrimaryButton,
  SecondaryButton,
} from 'components/common/buttons';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';

/** Component Imports */
import { styles } from './styles';
import UserImage from 'components/common/user-image';
import dateFormatter from 'util/dates/date-format';

/** Context Imports */
import { userContext } from 'context/user';
import { useNotification } from 'context/notification';
import { useConfirmation } from 'context/confirmation';
import { useLoader } from 'context/loader';
import useApplicationPermission from 'components/common/hooks/useCollabPermission';

/** Service Imports */
import {
  addUsersToProgram,
  deleteUserFromProgram,
  getProgramPendingAccessRequests,
  requestProgramAccess,
  sendEmailNotification,
  addPermissionToGitProgramRepo,
} from 'services/programs';

function ProgramTeam({ programId, updateProgramDetails }) {
  let classes = styles();
  const animatedComponents = makeAnimated();
  const { dispatchNotification } = useNotification();
  const { dispatchConfirmation } = useConfirmation();
  const { dispatchLoader } = useLoader();

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [pendingAccessRequests, setPendingAccessRequests] = useState([]);
  const groupedPendingAccessRequests = useMemo(() => {
    const requestMap = {};
    pendingAccessRequests.forEach((req) => {
      const daysOffset =
        new Date().getDate() - new Date(req.requestingTime).getDate();
      if (daysOffset === 0) {
        requestMap['Today'] = [...(requestMap['Today'] || []), req];
      } else if (daysOffset === 1) {
        requestMap['Yesterday'] = [...(requestMap['Yesterday'] || []), req];
      } else {
        requestMap[dateFormatter(req.requestingTime, false)] = [
          ...(requestMap['Yesterday'] || []),
          req,
        ];
      }
    });

    return requestMap;
  }, [pendingAccessRequests]);

  const [openSelfUserAccessChangeDialog, setOpenSelfUserAccessChangeDialog] =
    useState(false);
  const { currentUser } = useContext(userContext);
  const globalUsers = useSelector((state) => state.userReducer.globalUsers);
  const [requesterMessage, setRequesterMessage] = useState('');
  const [modifiedUserAccessType, setModifiedUserAccessType] = useState(1);
  const activeProgram = useSelector(
    (state) => state.programReducer.activeProgram,
  );
  const ownerId = useMemo(() => activeProgram.ownerId, [activeProgram.ownerId]);
  const coOwnerIds = useMemo(
    () => activeProgram.coOwnerIds,
    [activeProgram.coOwnerIds],
  );
  const { collabSuperAdminRole } = useApplicationPermission();
  const isCurrentUserCoOwner = useMemo(
    () => coOwnerIds?.includes(currentUser.userId) || false,
    [coOwnerIds, currentUser.userId],
  );

  const isAdmin = useMemo(
    () =>
      collabSuperAdminRole !== undefined ||
      ownerId === currentUser.userId ||
      false,
    // If user is part of super Admin group on collab platform (UM)
    [ownerId, currentUser.userId, collabSuperAdminRole],
  );

  const users = useMemo(() => activeProgram.users || [], [activeProgram.users]);
  const usersAccessLevel = useMemo(
    () => activeProgram.userAccessLevels || [],
    [activeProgram.userAccessLevels],
  );
  let usersList = useSelector((state) => {
    return globalUsers
      .filter((x) => x.userID !== '')
      .map((user) => {
        return {
          value: user.userID,
          label:
            user.firstName === '' && user.lastName === ''
              ? user?.email
              : `${user.firstName} ${user.lastName}`,
          data: user,
        };
      });
  });

  const programUsers = useMemo(() => {
    let usersTemp = users.map((user) => {
      let userDetails = globalUsers.find((x) => x.userID == user);
      if (userDetails) {
        return {
          userId: user,
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          email: userDetails?.email || '',
        };
      }
    });
    const usersTempList = usersTemp.filter(
      (userData) => userData !== undefined,
    );
    const ownerIndex = usersTempList.findIndex((use) => use.userId == ownerId);
    usersTempList.splice(0, 0, ...usersTempList.splice(ownerIndex, 1));

    return usersTempList;
  }, [users, globalUsers]);

  const handleAccessChangeByAcceptor = useCallback((type, index) => {
    setPendingAccessRequests((prev) => {
      prev[index].requesterAccess = type;
      return [...prev];
    });
  }, []);

  const handleSelfUserAccessChange = (selectedAccess) => {
    setOpenSelfUserAccessChangeDialog(true);
    setModifiedUserAccessType(selectedAccess);
  };

  const handleCloseSelfUserAccessChangeDialog = useCallback(() => {
    setOpenSelfUserAccessChangeDialog(false);
  }, []);

  const handleUserSelection = useCallback((updatedList) => {
    setSelectedUsers(updatedList);
  }, []);

  const addPermissionToGit = useCallback((userEmailList, programId) => {
    if (userEmailList.length > 0 && activeProgram.gitProgram) {
      addPermissionToGitProgramRepo(userEmailList, programId);
    }
  }, []);

  const handleUserAccessManagement = (
    id,
    type = 1,
    operationType = 'added',
    coOwner = false,
  ) => {
    console.log({ requestedId: id, current: currentUser.userId });
    // Added collabSuperAdminRole !== undefined so that super admin can approve own request.
    if (
      collabSuperAdminRole !== undefined ||
      (id !== currentUser.userId && id !== undefined)
    ) {
      let users = [];
      const successMessage =
        type == 'denied' ? 'denied access' : 'access approved';
      const approvalType =
        id == null || id == 'multiUserId'
          ? 'USER_ADDITION'
          : 'REQUEST_APPROVAL';
      users =
        approvalType === 'USER_ADDITION'
          ? selectedUsers.map((x) => x.value)
          : [id];
      let requesterDetails = globalUsers.find((x) => x.userID == id);
      const emailIdsToSendNotification =
        approvalType === 'USER_ADDITION'
          ? selectedUsers.map((user) => user.data.email)
          : [requesterDetails?.email];
      dispatchLoader({
        show: true,
        message: `${successMessage} to the program.`,
      });

      addUsersToProgram(
        programId,
        users,
        type,
        currentUser.userId,
        coOwner,
        collabSuperAdminRole !== undefined ? 0 : undefined,
      ).then((res) => {
        if (res.status === 'success') {
          updateProgramDetails().then((res1) => {
            dispatchLoader({ show: false });
            setSelectedUsers([]);
            dispatchNotification({
              show: true,
              severity: 'success',
              message:
                operationType === 'added'
                  ? `Successfully ${
                      coOwner ? `added as co-owner` : successMessage
                    } to the program`
                  : 'Successfully updated user access to the program',
            });
            emailIdsToSendNotification.forEach((email) => {
              const programUrl = `${
                process.env.REACT_APP_MFE_DESIGNER_ORIGIN ||
                window.location.origin
              }/collaboration/#/program/${programId}`;

              const emailDetails = {
                recipient_email: email,
                subject: `IPM Cloud Program Access Update: "${activeProgram.programName}"`,
                recipient_name: getRecipientName(email),
                program_link: programUrl,
                emailContent: `
                ${
                  type == 'denied'
                    ? `Your request has been denied`
                    : `You have been added to or your access level has been changed`
                } for the <b>"${
                  activeProgram.programName
                }"</b> program within the IPM Cloud platform by <b>"${
                  currentUser.firstName
                }"</b>.
  
                ${
                  type == 'denied'
                    ? ``
                    : `<br/><br/> You can access your program through the following link: ${programUrl}.`
                }
                <br/><br/>
                If you have any questions or require further assistance, please do not hesitate to contact us on email below.
                <br/><br/>
                Thank you for using IPM Cloud.<br/><br/>
                Best regards<br/><br/>`,
              };

              sendEmailNotification(emailDetails);
            });
            addPermissionToGit(emailIdsToSendNotification, programId);
          });
        } else {
          dispatchLoader({ show: false });
          dispatchNotification({
            show: true,
            severity: 'error',
            message: `Failed to provide access to the program. Error: ${res.error_message}`,
          });
        }
      });
    } else {
      // this block is used to request and modify user access by non owner
      var currentdate = new Date();
      var datetime =
        currentdate.getFullYear() +
        '-' +
        (currentdate.getMonth() + 1) +
        '-' +
        currentdate.getDate() +
        ' ' +
        currentdate.getHours() +
        ':' +
        currentdate.getMinutes() +
        ':' +
        currentdate.getSeconds();

      let userDetails = globalUsers.find((x) => x.userID == ownerId);
      let requestAccessDetails = {
        requesterName: `${currentUser.firstName} ${currentUser.lastName}`,
        requesterId: currentUser.userId,
        programName: activeProgram.programName,
        programId: activeProgram.programId,
        requestingTime: datetime,
        approverName: `${userDetails && userDetails.firstName} ${
          userDetails && userDetails.lastName
        }`,
        approverId: ownerId,
        requestedAccessLevel: modifiedUserAccessType,
        requesterMessage: requesterMessage,
        userId: currentUser.userId,
        accessRank: collabSuperAdminRole !== undefined ? 0 : undefined,
      };
      requestProgramAccess(requestAccessDetails).then((res) => {
        dispatchLoader({ show: false });
        if (res.status === 'success') {
          dispatchNotification({
            show: true,
            severity: 'success',
            message: 'Access level change requested successfully',
          });
        } else {
          dispatchNotification({
            show: true,
            severity: 'error',
            message: `Error: ${res.error_message}`,
          });
        }
      });
    }
  };

  const handleRequestMessage = useCallback((e) => {
    setRequesterMessage(e.target.value);
  }, []);

  const handleDeleteUserClick = useCallback((userId, userName) => {
    dispatchConfirmation({
      show: true,
      title: 'Remove user',
      message: `Do you really want to remove '${userName}' ?`,
      onConfirmation: () => {
        dispatchLoader({
          show: true,
          message: `Removing '${userName}' from the program`,
        });
        deleteUserFromProgram(programId, userId).then((res) => {
          if (res.status === 'success') {
            updateProgramDetails().then((res1) => {
              dispatchLoader({ show: false });
              dispatchNotification({
                show: true,
                severity: 'success',
                message: 'Successfully deleted the user from program',
              });
            });
          } else {
            dispatchLoader({ show: false });
            dispatchNotification({
              show: true,
              severity: 'error',
              message: `Failed to delete the user from program. Error: ${res.error_message}`,
            });
          }
        });
      },
    });
  }, []);

  useEffect(() => {
    getProgramPendingAccessRequests(
      ownerId,
      programId,
      currentUser.userId,
      collabSuperAdminRole !== undefined ? 0 : undefined,
    ).then((data) => {
      if (data.status === 'success') {
        //what is being done here? The requester data is gathered and the requester is eliminated if the data is undefined
        const { myProgramAccessRequests } = data;
        const myProgramsExistingRequesters = myProgramAccessRequests.map(
          (requester) => {
            const myProgramAccessRequesters = globalUsers.find(
              (user) => user.userID === requester.requesterId,
            );
            if (myProgramAccessRequesters) {
              return requester;
            }
          },
        );
        const myProgramExistingAccessRequesters =
          myProgramsExistingRequesters.filter(
            (requesterData) => requesterData !== undefined,
          );
        setPendingAccessRequests(myProgramExistingAccessRequesters);
      }
    });
  }, []);

  const colourStyles = {
    multiValue: (styles, { data }) => {
      return {
        ...styles,
        backgroundColor: 'rgb(225, 239, 255)',
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: 'rgb(1, 68, 138)',
    }),
  };

  const isTheDataForRequesterPresent = (x) => x.firstName && x.lastName;

  const isUserCowner = (userId) => activeProgram.coOwnerIds.includes(userId);

  const renderUserOptions = (user) => {
    const isCurrentUserInAnotherProgram = ownerId !== currentUser.userId;
    const isCurrentLoggeduserIsCoOwnerInProgram = isUserCowner(
      currentUser.userId,
    );
    const isTeamUserIsCurrentlyLoggedUser = currentUser.userId === user.userId;

    // If the current user is the owner of the program
    if (ownerId === user.userId) {
      return <span className={classes.ownerInfo}>Owner</span>;
    }

    // If the current user is not the program owner and is the user being rendered
    if (
      isCurrentUserInAnotherProgram &&
      isTeamUserIsCurrentlyLoggedUser &&
      !isCurrentLoggeduserIsCoOwnerInProgram &&
      collabSuperAdminRole === undefined
    ) {
      return (
        <span className={classes.userOptions}>
          <MuiSelect
            className={classes.ddlUserOptions}
            value={usersAccessLevel[user.userId]}
            onChange={(e) => handleSelfUserAccessChange(e.target.value)}
            disableUnderline
            IconComponent={KeyboardArrowDown}
          >
            <MenuItem value={2}>Full Access</MenuItem>
            <MenuItem value={1}>View Only</MenuItem>
          </MuiSelect>
        </span>
      );
    }

    // If the current user is an admin or a co-owner
    if (isAdmin || isCurrentLoggeduserIsCoOwnerInProgram) {
      return (
        <span className={classes.userOptions}>
          <MuiSelect
            className={classes.ddlUserOptions}
            value={usersAccessLevel[user.userId]}
            onChange={(e) => {
              if (e.target.value === 3) {
                handleDeleteUserClick(
                  user.userId,
                  `${user.firstName} ${user.lastName}`,
                );
              } else {
                handleUserAccessManagement(
                  user.userId,
                  e.target.value,
                  'edit',
                  isUserCowner(user.userId) && e.target.value === 2
                    ? true
                    : false,
                );
              }
            }}
            disableUnderline
            IconComponent={KeyboardArrowDown}
          >
            <MenuItem className={classes.ddlUserOptionsItem} value={2}>
              {isUserCowner(user.userId) ? 'Co-Owner' : 'Full Access'}
            </MenuItem>
            {
              <MenuItem
                className={classes.ddlUserOptionsItem}
                value={1}
                disabled={!isAdmin && !isCurrentLoggeduserIsCoOwnerInProgram}
              >
                View Only
              </MenuItem>
            }
            <MenuItem className={classes.ddlUserOptionsItem} value={3}>
              Remove from program
            </MenuItem>
          </MuiSelect>
        </span>
      );
    }

    // If none of the above conditions are met, don't render anything
    return null;
  };

  return (
    <div className={classes.root}>
      <div className={classes.addUserSection}>
        <span className={classes.cmbUserSelection}>
          <Select
            closeMenuOnSelect={false}
            components={animatedComponents}
            isMulti
            options={usersList.filter(
              (x) => !programUsers.find((y) => y.userId === x.value),
            )}
            formatOptionLabel={(option) => {
              return (
                <Tooltip
                  title={option?.data?.email || ''}
                  placement="right"
                  arrow
                >
                  <div>
                    {option.data.firstName === '' && option.data.lastName === ''
                      ? option.data.email
                      : `${option.data.firstName} ${option.data.lastName}`}
                  </div>
                </Tooltip>
              );
            }}
            placeholder="Add users"
            value={selectedUsers}
            onChange={handleUserSelection}
            //disable if you are not program admin or not co-admin
            isDisabled={!isAdmin && !isCurrentUserCoOwner}
            styles={colourStyles}
          />
        </span>
        <span className={classes.btnAddUser}>
          {(!isAdmin && !isCurrentUserCoOwner) || selectedUsers.length === 0 ? (
            <DisabledButton>Add to team</DisabledButton>
          ) : (
            <PrimaryButton
              onClick={() => handleUserAccessManagement('multiUserId')}
            >
              Add to team
            </PrimaryButton>
          )}
        </span>
        <span className={classes.btnAddUser}>
          {!isAdmin || selectedUsers.length === 0 ? (
            <DisabledButton>Add as co-owner</DisabledButton>
          ) : (
            <PrimaryButton
              onClick={() =>
                handleUserAccessManagement('multiUserId', 2, 'added', true)
              }
            >
              Add as co-owner
            </PrimaryButton>
          )}
        </span>
      </div>
      <div className={classes.usersSection}>
        <div className={classes.existingTeamMembersSection}>
          <div className={classes.sectionHeaderTitle}>
            Team Members ({programUsers.length})
          </div>
          <div>
            <ul className={classes.lstTeam}>
              {programUsers.length > 0 &&
                programUsers
                  .filter(
                    (user) =>
                      user !== undefined && Object.entries(user).length > 0,
                  )
                  .map((user, index) => (
                    <li key={`${user}-${index}`}>
                      <span className={classes.userImage}>
                        <UserImage
                          email={user?.email || ''}
                          firstName={user.firstName}
                          lastName={user.lastName}
                          bgColor="#1891F6"
                        />
                      </span>
                      <span className={classes.ownerName}>
                        {`${user.firstName} ${user.lastName}`}
                      </span>
                      {/* include the logic here */}

                      {renderUserOptions(user)}
                    </li>
                  ))}
            </ul>
          </div>
          <Dialog
            onClose={handleCloseSelfUserAccessChangeDialog}
            open={openSelfUserAccessChangeDialog}
          >
            <DialogTitle disableTypography>Modify Access Level</DialogTitle>
            <Divider />
            <DialogContent className={classes.dialogContent}>
              <TextareaAutosize
                placeholder="Explain your request"
                className={classes.messageText}
                value={requesterMessage}
                onChange={handleRequestMessage}
              />
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <SecondaryButton onClick={handleCloseSelfUserAccessChangeDialog}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={() => handleUserAccessManagement()}>
                Request Modify Access
              </PrimaryButton>
            </DialogActions>
          </Dialog>
        </div>
        <div className={classes.accessRequestsSection}>
          {isAdmin && pendingAccessRequests.length > 0 ? (
            <>
              <div className={classes.sectionHeaderTitle}>Access Requests</div>
              {Object.keys(groupedPendingAccessRequests).length > 0 &&
                Object.keys(groupedPendingAccessRequests).map(
                  (groupAccessReq) => {
                    const pendingReqsByGroup =
                      groupedPendingAccessRequests[groupAccessReq];
                    return (
                      <div
                        key={groupAccessReq}
                        className={classes.accessReqGroup}
                      >
                        <span className={classes.reqDate}>
                          {groupAccessReq}
                        </span>
                        <ul className={classes.lstAccessRequests}>
                          {pendingAccessRequests.length > 0 &&
                            Object.keys(groupedPendingAccessRequests).length >
                              0 &&
                            pendingReqsByGroup.map((request, index) => {
                              let reinfo = globalUsers.find(
                                (x) =>
                                  x.userID == request.requesterId &&
                                  isTheDataForRequesterPresent(x),
                              );
                              return (
                                <li key={`${request}-${index}`}>
                                  <div
                                    className={classes.accessRequestorSection}
                                  >
                                    <span className={classes.userImage}>
                                      <UserImage
                                        className={classes.userImage}
                                        email={reinfo?.email || ''}
                                        firstName={reinfo.firstName}
                                        lastName={reinfo.lastName}
                                        bgColor="#617080"
                                      />
                                    </span>
                                    <span
                                      className={classes.accessRequestorName}
                                    >
                                      {`${reinfo.firstName} ${reinfo.lastName}`}
                                    </span>
                                    <span
                                      className={
                                        classes.accessRequestActionButtons
                                      }
                                    >
                                      <SecondaryButton
                                        classes={{
                                          root: classes.btnAccessRequestAction,
                                        }}
                                        onClick={(accessType) =>
                                          handleUserAccessManagement(
                                            request.requesterId,
                                            'denied',
                                          )
                                        }
                                      >
                                        Deny
                                      </SecondaryButton>
                                      <PrimaryButton
                                        classes={{
                                          root: classes.btnAccessRequestAction,
                                        }}
                                        onClick={() =>
                                          handleUserAccessManagement(
                                            request.requesterId,
                                            request.requesterAccess,
                                          )
                                        }
                                      >
                                        Approve
                                      </PrimaryButton>
                                    </span>
                                  </div>
                                  <div
                                    className={
                                      classes.accessRequestLevelSection
                                    }
                                  >
                                    is requesting
                                    <span
                                      style={{
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                      }}
                                    >
                                      <MuiSelect
                                        className={
                                          classes.ddlAccessRequestLevelChange
                                        }
                                        value={request.requesterAccess}
                                        onChange={(e) =>
                                          handleAccessChangeByAcceptor(
                                            e.target.value,
                                            index,
                                          )
                                        }
                                        disableUnderline
                                        IconComponent={KeyboardArrowDown}
                                      >
                                        <MenuItem value={2}>
                                          Full Access
                                        </MenuItem>
                                        <MenuItem value={1}>View Only</MenuItem>
                                      </MuiSelect>
                                    </span>
                                  </div>
                                  <div className={classes.accessReasonSection}>
                                    <b>Reason: </b>
                                    {request.requesterMessage}
                                  </div>
                                </li>
                              );
                            })}
                        </ul>
                      </div>
                    );
                  },
                )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );

  function getRecipientName(email) {
    return (
      globalUsers.find((x) => x.email == email)?.firstName || 'IPM Cloud User'
    );
  }
}

export default memo(ProgramTeam);
